/**
 * Phone repair + validation for bulk imports (REDCap ODM/CSV, staff & patient uploads).
 *
 * Goes further than `normalizePhoneNumber` in phoneNormalization.ts: it tries to
 * REPAIR malformed source values before validating them, and reports exactly what
 * it changed so an import preview can show the operator every correction.
 *
 * Deliberately conservative: a repair is only applied when it produces a number
 * that matches the destination country's national-number rules. Anything that
 * stays ambiguous is returned as invalid with a reason rather than guessed.
 */

export type PhoneCountry =
  | 'ZA' | 'KE' | 'NG' | 'UG' | 'GH' | 'TZ' | 'EG' | 'ET'
  | 'RW' | 'MW' | 'ZM' | 'ZW' | 'ID' | 'BI' | 'SN';

interface CountryPlan {
  /** Dial code without '+'. */
  dial: string;
  /** Trunk prefix stripped before prepending the dial code (usually '0'). */
  trunk: string;
  /** Allowed national (subscriber) number lengths, excluding trunk prefix. */
  nationalLengths: number[];
  /** Leading digits of a valid national number. */
  nationalPrefixes: RegExp;
  /** Leading digits of a mobile national number. */
  mobilePrefixes: RegExp;
  name: string;
}

const PLANS: Record<PhoneCountry, CountryPlan> = {
  ZA: { dial: '27',  trunk: '0', nationalLengths: [9],     nationalPrefixes: /^[1-8]/,        mobilePrefixes: /^(6|7|8)/,                 name: 'South Africa' },
  KE: { dial: '254', trunk: '0', nationalLengths: [9],     nationalPrefixes: /^[1-7]/,        mobilePrefixes: /^(1|7)/,                   name: 'Kenya' },
  NG: { dial: '234', trunk: '0', nationalLengths: [10],    nationalPrefixes: /^[1-9]/,        mobilePrefixes: /^(70|701|8|9)/,            name: 'Nigeria' },
  UG: { dial: '256', trunk: '0', nationalLengths: [9],     nationalPrefixes: /^[2-9]/,        mobilePrefixes: /^(3|7)/,                   name: 'Uganda' },
  GH: { dial: '233', trunk: '0', nationalLengths: [9],     nationalPrefixes: /^[2-5]/,        mobilePrefixes: /^(2|5)/,                   name: 'Ghana' },
  TZ: { dial: '255', trunk: '0', nationalLengths: [9],     nationalPrefixes: /^[2-8]/,        mobilePrefixes: /^(6|7)/,                   name: 'Tanzania' },
  EG: { dial: '20',  trunk: '0', nationalLengths: [9, 10], nationalPrefixes: /^[1-9]/,        mobilePrefixes: /^1(0|1|2|5)/,              name: 'Egypt' },
  ET: { dial: '251', trunk: '0', nationalLengths: [9],     nationalPrefixes: /^[1-9]/,        mobilePrefixes: /^9/,                       name: 'Ethiopia' },
  RW: { dial: '250', trunk: '0', nationalLengths: [9],     nationalPrefixes: /^[2-7]/,        mobilePrefixes: /^7(2|3|8|9)/,              name: 'Rwanda' },
  MW: { dial: '265', trunk: '0', nationalLengths: [9],     nationalPrefixes: /^[1-9]/,        mobilePrefixes: /^(77|88|99|31)/,           name: 'Malawi' },
  ZM: { dial: '260', trunk: '0', nationalLengths: [9],     nationalPrefixes: /^[2-9]/,        mobilePrefixes: /^(76|77|95|96|97|75)/,     name: 'Zambia' },
  ZW: { dial: '263', trunk: '0', nationalLengths: [9],     nationalPrefixes: /^[1-9]/,        mobilePrefixes: /^7(1|3|7|8)/,              name: 'Zimbabwe' },
  ID: { dial: '62',  trunk: '0', nationalLengths: [9, 10, 11, 12], nationalPrefixes: /^[2-9]/, mobilePrefixes: /^8/,                      name: 'Indonesia' },
  BI: { dial: '257', trunk: '0', nationalLengths: [8],     nationalPrefixes: /^[2-7]/,        mobilePrefixes: /^(6|7)/,                   name: 'Burundi' },
  SN: { dial: '221', trunk: '0', nationalLengths: [9],     nationalPrefixes: /^[3-7]/,        mobilePrefixes: /^7(0|5|6|7|8)/,            name: 'Senegal' },
};

export type PhoneRepairCode =
  | 'stripped_formatting'
  | 'stripped_letters'
  | 'stripped_extension'
  | 'took_first_of_multiple'
  | 'converted_unicode_digits'
  | 'converted_00_prefix'
  | 'added_plus'
  | 'added_country_code'
  | 'added_missing_trunk_digit'
  | 'removed_duplicate_country_code'
  | 'removed_trunk_after_country_code';

export type PhoneRejectReason =
  | 'empty'
  | 'no_digits'
  | 'placeholder'
  | 'too_short'
  | 'too_long'
  | 'unknown_country'
  | 'invalid_national_number';

export interface PhoneRepairResult {
  /** E.164 value when valid, otherwise ''. */
  normalized: string;
  isValid: boolean;
  /** Original untouched input. */
  original: string;
  /** Country the number resolved to (may differ from the default). */
  country?: PhoneCountry;
  /** true when the number looks like a mobile line for its country. */
  isMobile: boolean;
  /** Machine-readable list of the corrections that were applied. */
  repairs: PhoneRepairCode[];
  /** Human-readable corrections, for import previews. */
  repairNotes: string[];
  /** Why the value was rejected, when isValid is false. */
  rejectReason?: PhoneRejectReason;
  /** Additional numbers found in the same cell (already repaired). */
  extras: string[];
}

const REPAIR_LABELS: Record<PhoneRepairCode, string> = {
  stripped_formatting: 'Removed spaces and punctuation',
  stripped_letters: 'Removed letters and words',
  stripped_extension: 'Removed extension',
  took_first_of_multiple: 'Cell held more than one number — used the first',
  converted_unicode_digits: 'Converted non-Latin digits',
  converted_00_prefix: 'Converted 00 international prefix to +',
  added_plus: 'Added missing +',
  added_country_code: 'Added country dialling code',
  added_missing_trunk_digit: 'Restored the missing leading 0',
  removed_duplicate_country_code: 'Removed a repeated country code',
  removed_trunk_after_country_code: 'Removed the trunk 0 after the country code',
};

/** Values REDCap sites commonly use to mean "no number". */
const PLACEHOLDERS = new Set([
  'na', 'n/a', 'none', 'nil', 'unknown', 'unk', 'nophone', 'nonumber',
  'notapplicable', 'notavailable', 'refused', 'declined', '-', '--', '.',
]);

const UNICODE_DIGIT_MAPS: Array<[number, number]> = [
  [0x0660, 0x0669], // Arabic-Indic
  [0x06f0, 0x06f9], // Extended Arabic-Indic
  [0x0966, 0x096f], // Devanagari
  [0xff10, 0xff19], // Fullwidth
];

function toLatinDigits(input: string): { text: string; changed: boolean } {
  let changed = false;
  const text = Array.from(input)
    .map((ch) => {
      const code = ch.codePointAt(0)!;
      for (const [start, end] of UNICODE_DIGIT_MAPS) {
        if (code >= start && code <= end) {
          changed = true;
          return String(code - start);
        }
      }
      return ch;
    })
    .join('');
  return { text, changed };
}

/** Split a cell that holds several numbers ("082 111 2222 / 073 444 5555"). */
export function splitPhoneCandidates(raw: string): string[] {
  return raw
    .split(/\s*(?:[;,/]|\bor\b|\band\b|\|)\s*/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

function isAllSameDigit(digits: string): boolean {
  return /^(\d)\1+$/.test(digits);
}

function isSequential(digits: string): boolean {
  return '01234567890'.includes(digits) || '09876543210'.includes(digits);
}

function nationalIsValid(plan: CountryPlan, national: string): boolean {
  return plan.nationalLengths.includes(national.length) && plan.nationalPrefixes.test(national);
}

function build(
  plan: CountryPlan,
  country: PhoneCountry,
  national: string,
  original: string,
  repairs: PhoneRepairCode[],
  extras: string[],
): PhoneRepairResult {
  return {
    normalized: `+${plan.dial}${national}`,
    isValid: true,
    original,
    country,
    isMobile: plan.mobilePrefixes.test(national),
    repairs,
    repairNotes: repairs.map((r) => REPAIR_LABELS[r]),
    extras,
  };
}

function reject(
  original: string,
  repairs: PhoneRepairCode[],
  rejectReason: PhoneRejectReason,
  extras: string[] = [],
): PhoneRepairResult {
  return {
    normalized: '',
    isValid: false,
    original,
    isMobile: false,
    repairs,
    repairNotes: repairs.map((r) => REPAIR_LABELS[r]),
    rejectReason,
    extras,
  };
}

/**
 * Repair one candidate value into E.164, without the multi-number splitting.
 */
function repairSingle(
  raw: string,
  defaultCountry: PhoneCountry,
  repairs: PhoneRepairCode[],
): PhoneRepairResult {
  const original = raw;

  const unicode = toLatinDigits(raw);
  let work = unicode.text;
  if (unicode.changed) repairs.push('converted_unicode_digits');

  // Drop extensions before we strip letters, so "x1234" doesn't merge into the number.
  const extStripped = work.replace(/\s*(?:ext\.?|extn\.?|x)\s*\d+\s*$/i, '');
  if (extStripped !== work) {
    repairs.push('stripped_extension');
    work = extStripped;
  }

  const lettersRemoved = work.replace(/[A-Za-z]/g, '');
  if (lettersRemoved !== work) {
    repairs.push('stripped_letters');
    work = lettersRemoved;
  }

  const hadPlus = /\+/.test(work);
  const digits = work.replace(/\D/g, '');
  if (work.replace(/[+\d]/g, '').length > 0) repairs.push('stripped_formatting');

  if (!digits) return reject(original, repairs, 'no_digits');
  if (isAllSameDigit(digits) || isSequential(digits)) {
    return reject(original, repairs, 'placeholder');
  }

  const plan = PLANS[defaultCountry];

  // 00 international prefix -> +
  let body = digits;
  if (/^00\d/.test(body)) {
    body = body.slice(2);
    repairs.push('converted_00_prefix');
    return fromInternational(body, original, repairs, defaultCountry, hadPlus);
  }

  if (hadPlus) {
    return fromInternational(body, original, repairs, defaultCountry, true);
  }

  // Starts with the default country's dial code but the + was lost.
  if (body.startsWith(plan.dial)) {
    const guess = fromInternational(body, original, [...repairs, 'added_plus'], defaultCountry, false);
    if (guess.isValid) return guess;
  }

  // Local format with trunk prefix: 0XXXXXXXXX
  if (plan.trunk && body.startsWith(plan.trunk)) {
    const national = body.slice(plan.trunk.length);
    if (nationalIsValid(plan, national)) {
      return build(plan, defaultCountry, national, original, repairs, []);
    }
  }

  // Bare national number, trunk prefix omitted.
  if (nationalIsValid(plan, body)) {
    return build(plan, defaultCountry, body, original, repairs, []);
  }

  // Leading 0 dropped by a spreadsheet: 82123456 (8) -> 0821234567 is not
  // recoverable, but a national number one digit short whose first digit is a
  // valid trunk-stripped start is. Only attempt when a single 0 restores it.
  if (plan.trunk === '0' && !body.startsWith('0')) {
    const withTrunk = '0' + body;
    const national = withTrunk.slice(1);
    if (nationalIsValid(plan, national) && national !== body) {
      return build(plan, defaultCountry, national, original, [...repairs, 'added_missing_trunk_digit'], []);
    }
  }

  const shortest = Math.min(...plan.nationalLengths);
  const longest = Math.max(...plan.nationalLengths);
  if (body.length < shortest) return reject(original, repairs, 'too_short');
  if (body.length > longest + plan.dial.length) return reject(original, repairs, 'too_long');
  return reject(original, repairs, 'invalid_national_number');
}

/** Handle a digit string that is meant to already carry a country code. */
function fromInternational(
  body: string,
  original: string,
  repairs: PhoneRepairCode[],
  defaultCountry: PhoneCountry,
  _hadPlus: boolean,
): PhoneRepairResult {
  const entries = (Object.entries(PLANS) as Array<[PhoneCountry, CountryPlan]>)
    // Longest dial code first so +254 wins over +2.
    .sort((a, b) => b[1].dial.length - a[1].dial.length);

  for (const [code, plan] of entries) {
    if (!body.startsWith(plan.dial)) continue;
    let rest = body.slice(plan.dial.length);
    const localRepairs = [...repairs];

    // "+2727821234567" — country code typed twice.
    if (rest.startsWith(plan.dial) && nationalIsValid(plan, rest.slice(plan.dial.length))) {
      rest = rest.slice(plan.dial.length);
      localRepairs.push('removed_duplicate_country_code');
    }

    // "+27 082 123 4567" — trunk 0 kept after the country code.
    if (plan.trunk && rest.startsWith(plan.trunk) && nationalIsValid(plan, rest.slice(plan.trunk.length))) {
      rest = rest.slice(plan.trunk.length);
      localRepairs.push('removed_trunk_after_country_code');
    }

    if (nationalIsValid(plan, rest)) {
      return build(plan, code, rest, original, localRepairs, []);
    }
  }

  // Unknown dial code but plausible E.164 — accept as-is rather than lose it.
  if (/^[1-9]\d{7,14}$/.test(body)) {
    return {
      normalized: `+${body}`,
      isValid: true,
      original,
      isMobile: false,
      repairs,
      repairNotes: repairs.map((r) => REPAIR_LABELS[r]),
      extras: [],
    };
  }

  // Maybe it never had a country code at all — fall back to the default plan.
  const plan = PLANS[defaultCountry];
  if (nationalIsValid(plan, body)) {
    return build(plan, defaultCountry, body, original, [...repairs, 'added_country_code'], []);
  }

  return reject(original, repairs, body.length < 8 ? 'too_short' : 'unknown_country');
}

/**
 * Repair and validate a raw phone value from an import file.
 *
 * @param raw            The cell value exactly as it appeared in the source.
 * @param defaultCountry Country of the site the data belongs to.
 */
export function repairPhoneNumber(
  raw: string | null | undefined,
  defaultCountry: PhoneCountry = 'ZA',
): PhoneRepairResult {
  if (raw == null) return reject('', [], 'empty');
  const trimmed = String(raw).trim();
  if (!trimmed) return reject(trimmed, [], 'empty');

  const bare = trimmed.toLowerCase().replace(/[\s._]/g, '');
  if (PLACEHOLDERS.has(bare)) return reject(trimmed, [], 'placeholder');

  const candidates = splitPhoneCandidates(trimmed);
  const repairs: PhoneRepairCode[] = [];
  if (candidates.length > 1) repairs.push('took_first_of_multiple');

  const primary = repairSingle(candidates[0] ?? trimmed, defaultCountry, repairs);

  // Repair the remaining numbers so the caller can keep them as secondary contacts.
  const extras: string[] = [];
  for (const candidate of candidates.slice(1)) {
    const result = repairSingle(candidate, defaultCountry, []);
    if (result.isValid && result.normalized !== primary.normalized) {
      extras.push(result.normalized);
    }
  }

  // If the first candidate was junk but a later one is good, promote it.
  if (!primary.isValid && extras.length > 0) {
    const promoted = repairSingle(candidates.find((c) => repairSingle(c, defaultCountry, []).isValid)!, defaultCountry, [
      ...repairs,
    ]);
    promoted.extras = extras.filter((e) => e !== promoted.normalized);
    promoted.original = trimmed;
    return promoted;
  }

  primary.original = trimmed;
  primary.extras = extras;
  return primary;
}

/** Convenience wrapper: E.164 string or '' when the value can't be repaired. */
export function repairPhoneOrEmpty(
  raw: string | null | undefined,
  defaultCountry: PhoneCountry = 'ZA',
): string {
  return repairPhoneNumber(raw, defaultCountry).normalized;
}

/** Aggregate counters for an import preview. */
export interface PhoneRepairSummary {
  total: number;
  valid: number;
  repaired: number;
  mobile: number;
  extras: number;
  rejected: number;
  rejectReasons: Record<string, number>;
  repairCounts: Record<string, number>;
}

export function summarizePhoneRepairs(results: PhoneRepairResult[]): PhoneRepairSummary {
  const summary: PhoneRepairSummary = {
    total: results.length,
    valid: 0,
    repaired: 0,
    mobile: 0,
    extras: 0,
    rejected: 0,
    rejectReasons: {},
    repairCounts: {},
  };

  for (const r of results) {
    if (r.isValid) {
      summary.valid += 1;
      if (r.repairs.length > 0) summary.repaired += 1;
      if (r.isMobile) summary.mobile += 1;
    } else {
      summary.rejected += 1;
      const reason = r.rejectReason ?? 'invalid_national_number';
      summary.rejectReasons[reason] = (summary.rejectReasons[reason] ?? 0) + 1;
    }
    summary.extras += r.extras.length;
    for (const code of r.repairs) {
      summary.repairCounts[code] = (summary.repairCounts[code] ?? 0) + 1;
    }
  }

  return summary;
}

export function describePhoneRejection(reason: PhoneRejectReason | undefined): string {
  switch (reason) {
    case 'empty': return 'No value in the source file';
    case 'no_digits': return 'No digits in the value';
    case 'placeholder': return 'Placeholder value (e.g. N/A, 0000000000)';
    case 'too_short': return 'Too few digits for a valid number';
    case 'too_long': return 'Too many digits for a valid number';
    case 'unknown_country': return 'Country dialling code not recognised';
    case 'invalid_national_number': return 'Digits do not match the country numbering plan';
    default: return 'Could not be validated';
  }
}
